import { InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'chakra-react-select';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { nationalityOptions, noop } from '@blockpulse3/data/shared';
import { DraftRepresentative, IdentityType, RepresentativeRole } from '@blockpulse3/graphql/hooks';
import { alpha3CountryOptions } from '@blockpulse3/helpers';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import {
  draftRepresentativeDefaultValues,
  spvNaturalRepresentativePresidentSchema,
  spvNaturalRepresentativeSchema,
} from '../../../provider/schema';
import { revocationMajorityOptions } from '../constants';

type Props = {
  /* ** Available roles for a representant form, must be Intl values ** */
  roles: (keyof IntlMessages['RoleValues'])[];
  /* ** Is role picker displayed ** */
  shouldDisplayRoles: boolean;
  /* ** Default form values ** */
  defaultValues?: DraftRepresentative;
  /* ** Is the Cancel button disabled ** */
  shouldCancel?: boolean;
  /* ** Callback of the Cancel button ** */
  onCancel?: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit: (data: DraftRepresentative) => void;
};

/**
 * NaturalRepresentantForm.
 * SPV.
 * Natural version of the RepresentantForm. Uses a forwardRef function to be able to submit it
 * from the outside.
 */
export const NaturalRepresentantInformationsForm = React.forwardRef<HTMLFormElement, Props>(
  (props, ref) => {
    const {
      roles,
      shouldDisplayRoles,
      defaultValues = { ...draftRepresentativeDefaultValues, type: IdentityType.INDIVIDUAL },
      shouldCancel = false,
      onCancel = noop,
      onSubmit,
    } = props;

    const i18nGeneral = useTranslations('General');
    const i18nRepresentantInformationsForm = useTranslations('NaturalRepresentantInformationsForm');
    const i18nRoleValues = useTranslations('RoleValues');
    const i18nRevocationMajorityValues = useTranslations('RevocationMajorityValues');

    const { register, control, formState, setValue, handleSubmit } = useForm<DraftRepresentative>({
      defaultValues,
      resolver: yupResolver(
        roles.includes(RepresentativeRole.PRESIDENT)
          ? spvNaturalRepresentativePresidentSchema
          : spvNaturalRepresentativeSchema,
      ),
    });

    const handleFormSubmit: SubmitHandler<DraftRepresentative> = (data): void => {
      onSubmit(data);
    };

    useEffect(() => {
      /* ** Fill fields on default values change: switching the it's me switch ** */
      const date = dayjs(defaultValues.birthdate).format('YYYY-MM-DD');
      setValue('firstName', defaultValues.firstName);
      setValue('lastName', defaultValues.lastName);
      setValue('birthdate', date);
      setValue('birthplace', defaultValues.birthplace);
      setValue('email', defaultValues.email);
      setValue('address', defaultValues.address);
      setValue('nationalityData', defaultValues.nationalityData);
    }, [defaultValues, defaultValues.firstName, setValue]);

    return (
      <form ref={ref} onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing="8">
          <Stack spacing="4">
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              {shouldDisplayRoles && (
                <Controller
                  control={control}
                  name="role"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.role} width="50%">
                      <FormLabel htmlFor="role">{i18nRoleValues('RoleField')}</FormLabel>
                      <RadioGroup id="role" {...field}>
                        <HStack spacing="4">
                          {roles.map((role) => (
                            <Radio key={role} value={role}>
                              {i18nRoleValues(role)}
                            </Radio>
                          ))}
                        </HStack>
                      </RadioGroup>
                      <ErrorMessage error={formState.errors.role} />
                    </FormControl>
                  )}
                />
              )}
              {roles.includes(RepresentativeRole.PRESIDENT) && (
                <Controller
                  control={control}
                  name="revocationMajorityData"
                  render={({ field: statusField }): JSX.Element => (
                    <FormControl
                      isInvalid={!!formState.errors?.revocationMajorityData}
                      maxWidth={{ base: 'full', md: '50%' }}
                    >
                      <FormLabel htmlFor="revocationMajorityData">
                        {i18nRepresentantInformationsForm('RevocationMajorityField')}
                      </FormLabel>
                      <Select
                        id="revocationMajorityData"
                        isSearchable={false}
                        menuPlacement="auto"
                        options={revocationMajorityOptions}
                        getOptionLabel={(option): string =>
                          option.label
                            ? i18nRevocationMajorityValues(
                                option.label as keyof IntlMessages['RevocationMajorityValues'],
                              )
                            : ''
                        }
                        {...statusField}
                      />
                      <ErrorMessage error={formState.errors?.revocationMajorityData?.value} />
                    </FormControl>
                  )}
                />
              )}
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.firstName}>
                <FormLabel htmlFor="firstName">
                  {i18nRepresentantInformationsForm('FirstNameField')}
                </FormLabel>
                <Input id="firstName" type="text" {...register('firstName')} />
                <ErrorMessage error={formState.errors?.firstName} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.lastName}>
                <FormLabel htmlFor="lastName">
                  {i18nRepresentantInformationsForm('LastNameField')}
                </FormLabel>
                <Input id="lastName" type="text" {...register('lastName')} />
                <ErrorMessage error={formState.errors?.lastName} />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.email}>
                <FormLabel htmlFor="email">
                  {i18nRepresentantInformationsForm('EmailField')}
                </FormLabel>
                <Input id="email" type="text" {...register('email')} />
                <ErrorMessage error={formState.errors?.email} />
              </FormControl>
              <Controller
                control={control}
                name="nationalityData"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.nationalityData}>
                    <FormLabel htmlFor="nationalityData">
                      {i18nRepresentantInformationsForm('NationalityField')}
                    </FormLabel>
                    <Select
                      id="nationalityData"
                      menuPlacement="auto"
                      options={nationalityOptions}
                      {...field}
                    />
                    <ErrorMessage error={formState.errors?.nationalityData?.value} />
                  </FormControl>
                )}
              />
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.birthdate}>
                <FormLabel htmlFor="birthdate">
                  {i18nRepresentantInformationsForm('BirthdateField')}
                </FormLabel>
                <Input id="birthdate" type="date" {...register('birthdate')} />
                <ErrorMessage error={formState.errors?.birthdate} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.birthplace}>
                <FormLabel htmlFor="birthplace">
                  {i18nRepresentantInformationsForm('BirthplaceField')}
                </FormLabel>
                <Input id="birthplace" type="text" {...register('birthplace')} />
                <ErrorMessage error={formState.errors?.birthplace} />
              </FormControl>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Text fontSize="lg" fontWeight="bold">
                {i18nRepresentantInformationsForm('Address.Title')}
              </Text>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.line}>
                    <FormLabel htmlFor="line">
                      {i18nRepresentantInformationsForm('Address.LineField')}
                    </FormLabel>
                    <Input id="line" type="string" {...register('address.line')} />
                    <ErrorMessage error={formState.errors?.address?.line} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.address?.city}>
                    <FormLabel htmlFor="city">
                      {i18nRepresentantInformationsForm('Address.CityField')}
                    </FormLabel>
                    <Input id="city" type="string" {...register('address.city')} />
                    <ErrorMessage error={formState.errors?.address?.city} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.address?.postalCode}>
                    <FormLabel htmlFor="zipcode">
                      {i18nRepresentantInformationsForm('Address.ZipCodeField')}
                    </FormLabel>
                    <Input id="zipcode" type="string" {...register('address.postalCode')} />
                    <ErrorMessage error={formState.errors?.address?.postalCode} />
                  </FormControl>
                  <Controller
                    control={control}
                    name="address.countryData"
                    render={({ field }): JSX.Element => (
                      <FormControl isInvalid={!!formState.errors.address?.countryData}>
                        <FormLabel htmlFor="country">
                          {i18nRepresentantInformationsForm('Address.CountryField')}
                        </FormLabel>
                        <Select
                          id="country"
                          menuPlacement="auto"
                          options={alpha3CountryOptions}
                          {...field}
                        />
                        <ErrorMessage error={formState.errors.address?.countryData?.value} />
                      </FormControl>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <HStack alignItems="flex-start" spacing="2">
                <Text fontSize="lg" fontWeight="bold">
                  {i18nRepresentantInformationsForm('FiliationTitle')}
                </Text>
                <Tooltip
                  hasArrow
                  label={i18nRepresentantInformationsForm('Tooltip.Filiation')}
                  placement="top"
                >
                  <Icon as={InfoIcon} color="gray.500" />
                </Tooltip>
              </HStack>
              <Stack spacing="4">
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.fatherFirstName}>
                    <FormLabel htmlFor="fatherFirstName">
                      {i18nRepresentantInformationsForm('FatherFirstNameField')}
                    </FormLabel>
                    <Input id="fatherFirstName" type="text" {...register('fatherFirstName')} />
                    <ErrorMessage error={formState.errors?.fatherFirstName} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.fatherLastName}>
                    <FormLabel htmlFor="fatherLastName">
                      {i18nRepresentantInformationsForm('FatherLastNameField')}
                    </FormLabel>
                    <Input id="fatherLastName" type="text" {...register('fatherLastName')} />
                    <ErrorMessage error={formState.errors?.fatherLastName} />
                  </FormControl>
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.motherFirstName}>
                    <FormLabel htmlFor="motherFirstName">
                      {i18nRepresentantInformationsForm('MotherFirstNameField')}
                    </FormLabel>
                    <Input id="motherFirstName" type="text" {...register('motherFirstName')} />
                    <ErrorMessage error={formState.errors?.motherFirstName} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.motherLastName}>
                    <FormLabel htmlFor="motherLastName">
                      {i18nRepresentantInformationsForm('MotherLastNameField')}
                    </FormLabel>
                    <Input id="motherLastName" type="text" {...register('motherLastName')} />
                    <ErrorMessage error={formState.errors?.motherLastName} />
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Button
              isDisabled={shouldCancel}
              type="button"
              variant="secondary"
              w="full"
              onClick={onCancel}
            >
              {i18nGeneral('Delete')}
            </Button>
            <Button type="submit" w="full">
              {i18nGeneral('Submit')}
            </Button>
          </Stack>
        </Stack>
      </form>
    );
  },
);

NaturalRepresentantInformationsForm.displayName = 'NaturalRepresentantInformationsForm';

export type NaturalRepresentantInformationsFormProps = Props;
